
import React, { useEffect, useState, useMemo } from "react";
import copyToClipboard from "copy-to-clipboard";
import FeedCommonFooter from "../components/FeedFooter";
import FeedListContent from "../components/FeedListContent";
import FeedBodyHeader from "../FeedList/components/FeedHeader";
import { FlashMessage } from "../../../components/common";
import { Helmet } from "react-helmet";
import eh_logo from "../../../assets/eh_logo.png";
import Skeleton from "@material-ui/lab/Skeleton";
import { rootReducersState } from "../../../reducers";
import {
  getFeedDetailsRequest,
  postLikeRequest,
  updateFeedDetailsRequest,
  postLikeReset,
  updatePostFlagReset,
  commentsCreateFlagReset,
  commentLikeReset,
  changeReplyData,
} from "../../../reducers/feeds/feedPost.reducer";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import appRoutes from "../../../routes/app.routes";
import { useNavigate, useParams } from "react-router-dom";
import { ReducerFlag } from "../../../types/reducer.types";
import ArticleDetailsView from "../components/ArticleView";
import API_URLS from "../../../utils/apiUrls";
import httpRequest from "../../../utils/httpRequest";

const FeedDetails = () => {
  const params = useParams();
  const [previewImage, setPreviewImage] = useState<string>(eh_logo);
  const [localPostDetails, setLocalPostDetails] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const feedPostData = useSelector(({ postFeed }: rootReducersState) => postFeed);
  const sessionReducer = useSelector(({ session }: rootReducersState) => session);
  
  const { 
    updateFlag, 
    updateMessage,
    commentCreateFlag,
    replyCreateFlag,
    replyData,
    commentLikeFlag,
    commentLikedData,
    newCommentData,
    replyDeleteFlag
  } = useSelector(({ postFeed }: rootReducersState) => postFeed.postFeed);

  const postDetails = _.get(feedPostData, "postFeed.detail", {});
  const detailsLoading = _.get(feedPostData, "postFeed.feedDetailsLoading", "");
  const detailsFlag = _.get(feedPostData, "postFeed.postDetailFlag", "");
  const likeFlag = _.get(feedPostData, "postFeed.postLikeFlag", false);
  const postFeedLikeData = _.get(feedPostData, "postFeed.likedData", {});

  const isArticle = Number(postDetails?.post_type) === 1;
  // Update local state when postDetails changes from Redux
  useEffect(() => {
    if (postDetails && Object.keys(postDetails).length > 0) {
      setLocalPostDetails(postDetails);
    }
  }, [postDetails]);


  useEffect(() => {
    const currentDetails =
      Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;

    if (!currentDetails || Object.keys(currentDetails).length === 0) return;

    const isArt = Number(currentDetails?.post_type) === 1;
    const isLoggedIn = Boolean(_.get(sessionReducer, "currentUser.id"));

    if (!isArt && !isLoggedIn) {
      // include return path so user can be redirected back after login
      navigate(appRoutes.userLogin.path, {
        state: { redirectTo: window.location.pathname },
      });
    }
  }, [localPostDetails, postDetails, sessionReducer, navigate]);


  // Derive best preview image (cover > first content media > first post_media > fallback)
  const derivedPreview = useMemo(() => {
    const currentPostDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;
    
    if (isArticle) {
      if (currentPostDetails?.cover_image) return currentPostDetails.cover_image;

      const cm = currentPostDetails?.content_media_processed || {};
      const firstKey = Object.keys(cm)[0];
      if (firstKey && cm[firstKey]) {
        return cm[firstKey].thumbnailSignedUrl || cm[firstKey].signedUrl || eh_logo;
      }
    }

    const firstPostMedia = _.get(currentPostDetails, "post_media[0]");
    if (firstPostMedia) {
      return firstPostMedia.media_thumbnail || firstPostMedia.media_original || eh_logo;
    }

    return eh_logo;
  }, [isArticle, localPostDetails, postDetails]);

  useEffect(() => {
    setPreviewImage(derivedPreview);
  }, [derivedPreview]);

  useEffect(() => {
    dispatch(getFeedDetailsRequest(_.get(params, "slug", "")));
         // eslint-disable-next-line
  }, []);

  
  useEffect(() => {
  if (replyDeleteFlag === ReducerFlag.SUCCESS && replyData) {
    const currentDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;

    const updatedComments = (currentDetails.post_comments || []).map((comment) => {
      if (comment.id === parseInt(replyData.parentId)) {
        const filteredReplies = (comment.reply_comments || []).filter(
          (reply) => reply.id !== replyData.id
        );
        return {
          ...comment,
          reply_comments: filteredReplies,
          reply_count: Math.max((comment.reply_count || 1) - 1, 0),
        };
      }
      return comment;
    });

    const updatedPostDetails = {
      ...currentDetails,
      post_comments: updatedComments,
      post_replies_count: Math.max((currentDetails.post_replies_count || 1) - 1, 0),
    };

    setLocalPostDetails(updatedPostDetails);
    // Optionally update Redux as well:
    dispatch(updateFeedDetailsRequest(updatedPostDetails));
    
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [replyDeleteFlag]);

  useEffect(() => {
    if ([ReducerFlag.SUCCESS, ReducerFlag.FAILED].includes(updateFlag)) {
      if (updateFlag === ReducerFlag.SUCCESS) {
        dispatch(getFeedDetailsRequest(_.get(params, "slug", "")));
      } else if (updateFlag === ReducerFlag.FAILED) {
        FlashMessage(updateMessage, "error");
      }
      dispatch(updatePostFlagReset());
    }
         // eslint-disable-next-line
  }, [updateFlag]);

  // Handle new comment creation - FIXED
  useEffect(() => {
    if (commentCreateFlag === ReducerFlag.SUCCESS && newCommentData) {
      const currentUser = {
        id: _.get(sessionReducer, "currentUser.id", ""),
        first_name: _.get(sessionReducer, "currentUser.first_name", ""),
        last_name: _.get(sessionReducer, "currentUser.last_name", ""),
        profile_image: _.get(sessionReducer, "currentUser.profile_image", ""),
        slug: _.get(sessionReducer, "currentUser.slug", ""),
      };

      const newComment = {
        ...newCommentData,
        user: currentUser,
        liked: 0,
        reply_count: 0,
        reply_comments: [], // Initialize empty replies
        parent_id: null,
        comment_likes_count: 0,
      };

      const currentDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;
      
      // Check if comment already exists to prevent duplicates
      const existingComment = currentDetails.post_comments?.find(comment => comment.id === newComment.id);
      if (existingComment) {
        dispatch(commentsCreateFlagReset());
        return;
      }

      // Update local state immediately - ADD TO BEGINNING
      const updatedPostDetails = {
        ...currentDetails,
        post_comments: [newComment, ...(currentDetails.post_comments || [])],
        post_comments_count: (currentDetails.post_comments_count || 0) + 1,
      };

      setLocalPostDetails(updatedPostDetails);
      dispatch(updateFeedDetailsRequest(updatedPostDetails));
      dispatch(commentsCreateFlagReset());
    }
         // eslint-disable-next-line
  }, [commentCreateFlag, newCommentData, localPostDetails, postDetails, sessionReducer, dispatch]);

  // Handle new reply creation - FIXED
  useEffect(() => {
    if (replyCreateFlag === ReducerFlag.SUCCESS && replyData) {
      const currentUser = {
        id: _.get(sessionReducer, "currentUser.id", ""),
        first_name: _.get(sessionReducer, "currentUser.first_name", ""),
        last_name: _.get(sessionReducer, "currentUser.last_name", ""),
        profile_image: _.get(sessionReducer, "currentUser.profile_image", ""),
        slug: _.get(sessionReducer, "currentUser.slug", ""),
      };

      const newReply = {
        ...replyData,
        user: currentUser,
        liked: 0,
        comment_likes_count: 0,
      };

      const currentDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;
      
      const updatedComments = (currentDetails.post_comments || []).map((comment) => {
        if (comment.id === parseInt(replyData.parent_id)) {
          // Check if reply already exists
          const existingReply = comment.reply_comments?.find(reply => reply.id === newReply.id);
          if (existingReply) {
            return comment;
          }
          
          return {
            ...comment,
            reply_count: (comment.reply_count || 0) + 1,
            reply_comments: [...(comment.reply_comments || []), newReply], // Add to end
          };
        }
        return comment;
      });

      // Update local state immediately
      const updatedPostDetails = {
        ...currentDetails,
        post_comments: updatedComments,
        post_replies_count: (currentDetails.post_replies_count || 0) + 1,
      };

      setLocalPostDetails(updatedPostDetails);
      dispatch(updateFeedDetailsRequest(updatedPostDetails));
      dispatch(commentsCreateFlagReset());
    }
  }, [replyCreateFlag, replyData, localPostDetails, postDetails, sessionReducer, dispatch]);

  // Handle comment like/unlike - FIXED
  useEffect(() => {
    if (commentLikeFlag === ReducerFlag.SUCCESS && commentLikedData) {
      const currentDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;
           if (commentLikedData.parentId === null) {
        // Main comment like
        const updatedComments = (currentDetails.post_comments || []).map((comment) => {
          if (comment.id === commentLikedData.commentId) {
            return {
              ...comment,
              liked: commentLikedData.liked,
              comment_likes_count: commentLikedData.totalLikes,
            };
          }
          return comment;
        });

        const updatedPostDetails = {
          ...currentDetails,
          post_comments: updatedComments,
        };

        setLocalPostDetails(updatedPostDetails);
        dispatch(updateFeedDetailsRequest(updatedPostDetails));
      } else {
        // Reply like – use type-safe id comparison (API may return number or string)
        const parentId = Number(commentLikedData.parentId);
        const replyCommentId = commentLikedData.commentId;
        const updatedComments = (currentDetails.post_comments || []).map((comment) => {
          if (Number(comment.id) === parentId) {
            const updatedReplies = (comment.reply_comments || []).map((reply) => {
              if (Number(reply.id) === Number(replyCommentId)) {
                return {
                  ...reply,
                  liked: commentLikedData.liked,
                  comment_likes_count: commentLikedData.totalLikes,
                };
              }
              return reply;
            });
            return { ...comment, reply_comments: updatedReplies };
          }
          return comment;
        });

        const updatedPostDetails = {
          ...currentDetails,
          post_comments: updatedComments,
        };

        setLocalPostDetails(updatedPostDetails);
        dispatch(updateFeedDetailsRequest(updatedPostDetails));
      }
      dispatch(commentLikeReset());
    }
  }, [commentLikeFlag, commentLikedData, localPostDetails, postDetails, dispatch]);

  // Post like/unlike hook
  useEffect(() => {
    if (likeFlag === ReducerFlag.SUCCESS) {
      const currentDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;
      const updatedPostDetails = {
        ...currentDetails,
        liked: postFeedLikeData.liked,
        post_likes_count: postFeedLikeData.totalLikes,
      };
      
      setLocalPostDetails(updatedPostDetails);
      dispatch(updateFeedDetailsRequest(updatedPostDetails));
      dispatch(postLikeReset());
    }
  }, [likeFlag, postFeedLikeData, localPostDetails, postDetails, dispatch]);

  useEffect(() => {
    if (detailsFlag === ReducerFlag.FAILED) {
      navigate(appRoutes.Feed.path);
    }
  }, [detailsFlag, navigate]);

  const handlePostLike = (pd: any) => {
    dispatch(postLikeRequest({ post_id: pd.id }));
  };

  const handleCopyPostLink = (pd: any) => {
    const postLink = appRoutes.FeedDetails.generateFullPath(pd.slug);
    copyToClipboard(postLink);
    FlashMessage("Copied post link");
  };

  useEffect(() => {
    if (Object.keys(postDetails).length > 0) {
      filterFetchData();
    }
         // eslint-disable-next-line
  }, [postDetails?.id]);

//   const filterFetchData = () => {
//   let listData = postDetails;

//   // Make sure listData is always an array
//   if (!Array.isArray(listData)) {
//     listData = listData ? [listData] : [];
//   }

//   let replyList = [];

//   listData.forEach((post) => {
//     const comments = _.get(post, "post_comments", []);

//     comments.forEach((comment) => {
//       const replies = _.get(comment, "reply_comments", []);

//       replyList.push({
//         post_id: _.get(post, "id", ""),
//         comment_id: _.get(comment, "id", "").toString(),
//         count: replies.length,
//         rows: replies.map((reply) => ({
//           id: _.get(reply, "id", ""),
//           post_id: _.get(reply, "post_id", ""),
//           parent_id: _.get(reply, "parent_id", ""),
//           comment_text: _.get(reply, "comment_text", ""),
//           comment_type: _.get(reply, "comment_type", ""),
//           liked: _.get(reply, "liked", 0),
//           comment_likes_count: _.get(reply, "comment_likes_count", 0),
//           created_at: _.get(reply, "created_at", ""),
//           updated_at: _.get(reply, "updated_at", ""),
//           is_edited: _.get(reply, "is_edited", false),
//           formatted_edit_time: _.get(reply, "formatted_edit_time", null),
//           user: _.get(reply, "user", {}),
//         })),
//       });
//     });
//   });

//   dispatch(changeReplyData(replyList));
// };

const filterFetchData = () => {
  // Ensure postDetails is always an array
  let listData = Array.isArray(postDetails) ? postDetails : postDetails ? [postDetails] : [];
  
  let replyList = [];
  
  listData.forEach((post) => {
    let comments = _.get(post, "post_comments", []);
    
    comments.forEach((comment) => {
      let replies = _.get(comment, "reply_comments", []);
      
      replyList.push({
        count: replies.length,
        rows: [],
        comment_id: _.get(comment, "id", "").toString(),
        post_id: _.get(post, "id", ""),
      });
    });
  });
  
  dispatch(changeReplyData(replyList));
};


  // Use local state for rendering
  const currentPostDetails = Object.keys(localPostDetails).length > 0 ? localPostDetails : postDetails;

  const toggleBookmark = async (postId) => {
  try {
       const res =     await httpRequest().post(API_URLS.toggleBookmarkByPostId(postId.id));
    const data = res?.data?.data || {};

    // update local detail
    setLocalPostDetails((prev) => ({
      ...prev,
      bookmarked: !!data.bookmarked,
      post_bookmarks_count: Number(data.totalBookmarks ?? prev?.post_bookmarks_count ?? 0),
    }));

    FlashMessage(data.bookmarked ? "Saved to bookmarks" : "Removed from bookmarks", "success");
  } catch (error) {
    FlashMessage(error?.response?.data?.message || "Bookmark failed", "error");
  }
};

  return (
    <>
      <Helmet>
        <meta property="og:image" content={previewImage} />
        <meta property="twitter:image" content={previewImage} />
      </Helmet>

      <div className="max-container">
        <div className="details-container">
          {detailsLoading || detailsFlag !== ReducerFlag.SUCCESS ? (
            <div className="p-20">
              <Skeleton width="40%" height={36} variant="text" />
              <Skeleton width="100%" height={280} variant="rect" />
              <Skeleton width="90%" variant="text" />
              <Skeleton width="95%" variant="text" />
            </div>
          ) : isArticle ? (
            <ArticleDetailsView
              data={currentPostDetails}
              onLike={() => handlePostLike(currentPostDetails)}
              onCopy={() => handleCopyPostLink(currentPostDetails)}
                onBookmark={() => toggleBookmark(currentPostDetails)} 
            />
          ) : (
            <div className="feed-content feed-details-content">
              <FeedBodyHeader data={currentPostDetails} />
              <FeedListContent data={currentPostDetails} />
              <FeedCommonFooter
                data={currentPostDetails}
                postLike={(d) => handlePostLike(d)}
                handleCopyPostLink={(d) => handleCopyPostLink(d)}
                defaultShowComments={true}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedDetails;
